// src/components/common/hooks/useEntityForm.ts
import { useState, useEffect, useCallback } from 'react';

/** Form field value: covers all common HTML form value types */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FormValue = any;

export interface FieldConfig {
  name: string;
  label: string;
  type: 'text' | 'email' | 'date' | 'select' | 'textarea' | 'checkbox' | 'number' | 'tel';
  required?: boolean;
  options?: { value: string | number | boolean; label: string }[];
  validation?: (value: FormValue) => string | null;
  group?: string;
  span?: number;
  colSpan?: number; // Alias for span
  render?: (value: FormValue, entity: Record<string, FormValue>) => string;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  min?: number;
  max?: number;
}

export interface EditableField {
  field: string;
  isEditing: boolean;
}

export interface PendingChange {
  type: 'add' | 'remove' | 'modify';
  section: string;
  id?: number;
  data?: Record<string, unknown>;
}

interface UseEntityFormOptions<T> {
  entity: T | null;
  fields: FieldConfig[];
  onSave?: (data: Record<string, FormValue>) => Promise<T>;
  transformToFormData?: (entity: T) => Record<string, FormValue>;
  transformFromFormData?: (formData: Record<string, FormValue>) => Record<string, FormValue>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useEntityForm = <T extends Record<string, any>>({
  entity,
  fields,
  onSave,
  transformToFormData,
  transformFromFormData,
}: UseEntityFormOptions<T>) => {
  const [formData, setFormData] = useState<Record<string, FormValue> | null>(null);
  const [originalData, setOriginalData] = useState<Record<string, FormValue> | null>(null);
  const [editableFields, setEditableFields] = useState<EditableField[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialiser formData quand entity change
  useEffect(() => {
    const data = transformToFormData ? transformToFormData(entity) : entity;
    setFormData(data);
    setOriginalData(data);
    setHasChanges(false);
    setPendingChanges([]);
    setEditableFields([]);
  }, [entity]);

  // Gérer le changement d'un champ
  const handleFieldChange = useCallback((field: string, value: FormValue) => {
    // Calculer le nouveau formData en dehors de l'updater (évite les side effects)
    const newData = formData ? { ...formData, [field]: value } : { [field]: value };

    setFormData(newData);

    // Mettre à jour hasChanges en dehors de l'updater de setFormData
    if (originalData) {
      const hasFormChanges = JSON.stringify(newData) !== JSON.stringify(originalData);
      setHasChanges(hasFormChanges || pendingChanges.length > 0);
    }

    // Valider le champ
    const fieldConfig = fields.find(f => f.name === field);
    if (fieldConfig?.validation) {
      const error = fieldConfig.validation(value);
      setErrors(prev => ({
        ...prev,
        [field]: error || '',
      }));
    }
  }, [fields, formData, originalData, pendingChanges]);

  // Commencer l'édition d'un champ
  const startEditing = useCallback((field: string) => {
    setEditableFields(prev => [
      ...prev.filter(f => f.field !== field),
      { field, isEditing: true }
    ]);
  }, []);

  // Arrêter l'édition d'un champ
  const stopEditing = useCallback((field: string) => {
    setEditableFields(prev => prev.filter(f => f.field !== field));
  }, []);

  // Vérifier si un champ est en édition
  const isEditing = useCallback((field: string) => {
    return editableFields.some(f => f.field === field && f.isEditing);
  }, [editableFields]);

  // Ajouter un changement en attente
  const addPendingChange = useCallback((change: PendingChange) => {
    setPendingChanges(prev => [...prev, change]);
    setHasChanges(true);
  }, []);

  // Supprimer un changement en attente
  const removePendingChange = useCallback((index: number) => {
    const newChanges = pendingChanges.filter((_, i) => i !== index);
    setPendingChanges(newChanges);
    if (originalData && formData) {
      const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
      setHasChanges(newChanges.length > 0 || hasFormChanges);
    } else {
      setHasChanges(newChanges.length > 0);
    }
  }, [pendingChanges, formData, originalData]);

  // Sauvegarder les changements
  const handleSave = useCallback(async () => {
    if (!onSave || !formData) return;

    // Valider tous les champs requis
    const validationErrors: Record<string, string> = {};
    fields.forEach(field => {
      const val = formData[field.name];
      const isEmpty = val === null || val === undefined || val === '';
      if (field.required && isEmpty) {
        validationErrors[field.name] = `${field.label} est requis`;
      }
      if (field.validation) {
        const error = field.validation(formData[field.name]);
        if (error) {
          validationErrors[field.name] = error;
        }
      }
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const dataToSave = transformFromFormData
        ? transformFromFormData(formData)
        : formData;

      const savedEntity = await onSave(dataToSave);

      // Réinitialiser les états après sauvegarde
      const newData = transformToFormData ? transformToFormData(savedEntity) : savedEntity;
      setFormData(newData);
      setOriginalData(newData);
      setHasChanges(false);
      setPendingChanges([]);
      setEditableFields([]);
      setErrors({});

      return savedEntity;
    } catch (error) {
      throw error;
    } finally {
      setSaving(false);
    }
  }, [formData, fields, onSave, transformFromFormData, transformToFormData]);

  // Annuler les changements
  const handleCancel = useCallback(() => {
    if (originalData) {
      setFormData(originalData);
      setHasChanges(false);
      setPendingChanges([]);
      setEditableFields([]);
      setErrors({});
    }
  }, [originalData]);

  return {
    formData,
    hasChanges,
    pendingChanges,
    editableFields,
    saving,
    errors,
    handleFieldChange,
    startEditing,
    stopEditing,
    isEditing,
    addPendingChange,
    removePendingChange,
    handleSave,
    handleCancel,
    setFormData,
    setHasChanges,
  };
};
